import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Loader2, Users, UserCheck, Clock, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { userService } from '..';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';
import TeamMemberCard from '../components/TeamMemberCard';

const fmtDate = (ts) => {
  if (!ts) return 'N/A';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return 'N/A'; }
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b last:border-0"
    style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
    <span className="w-28 flex-shrink-0 text-xs font-semibold uppercase tracking-wide pt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>{label}</span>
    <span className="text-sm flex-1 break-words" style={{ color: NEUTRAL_COLORS.black }}>{value || 'N/A'}</span>
  </div>
);

const Team = () => {
  const { user, hasPermission, hasRole } = useApiAuth();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await userService.getActiveUsers();
      const all = Array.isArray(res) ? res : (res?.users || []);
      setMembers(
        all
          .filter(u => u.role !== 'client')
          .map(u => ({
            id: u.id,
            name: u.name || '',
            email: u.email || '',
            role: u.role || '',
            phone: u.phone || '',
            skills: u.skills || [],
            status: u.status || 'active',
            photo_url: u.photo_url || null,
            profileImage: u.photo_url || null,
            createdAt: u.created_at,
            lastLoginAt: u.last_login_at,
          }))
      );
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    return (
      (!q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)) &&
      (filterStatus === 'all' || m.status === filterStatus)
    );
  });

  const handleDelete = async (id) => {
    if (!hasPermission('users.delete') && !hasRole('admin')) {
      toast.error('You do not have permission to remove members');
      return;
    }
    try {
      await userService.deleteUser(id);
      setMembers(prev => prev.filter(m => m.id !== id));
      toast.success('Member removed');
    } catch { toast.error('Failed to remove member'); }
  };

  const handleViewProfile = async (id) => {
    setProfileLoading(true);
    setSelectedProfile({});
    try {
      const profile = await userService.getUserById(id);
      setSelectedProfile(profile);
    } catch {
      toast.error('Failed to load profile');
      setSelectedProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const stats = [
    { label: 'Total Members', value: members.length,                                    icon: Users,     color: BRAND_COLORS.primary,   tint: SYSTEM_COLORS.primaryTint  },
    { label: 'Active',        value: members.filter(m => m.status === 'active').length,  icon: UserCheck, color: SYSTEM_COLORS.success,   tint: SYSTEM_COLORS.successTint  },
    { label: 'On Leave',      value: members.filter(m => m.status === 'onLeave').length, icon: Clock,     color: SYSTEM_COLORS.warning,   tint: SYSTEM_COLORS.warningTint  },
  ];

  return (
    <div className="space-y-6">

      {/* Stats row */}
      {!loading && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {stats.map(({ label, value, icon: Icon, color, tint }) => (
            <div key={label}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border sm:px-5"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-xl"
                style={{ backgroundColor: tint }}>
                <Icon size={16} style={{ color }} />
              </div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ color }}>{value}</p>
                <p className="text-[11px] mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex items-center flex-1 gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Search size={15} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="flex-1 text-sm bg-transparent outline-none"
            style={{ color: NEUTRAL_COLORS.black }}
          />
          {search && (
            <button onClick={() => setSearch('')}>
              <X size={13} style={{ color: SYSTEM_COLORS.textSoft }} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 sm:w-44"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Filter size={13} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="flex-1 text-sm bg-transparent outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="onLeave">On Leave</option>
            <option value="dormant">Dormant</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-20">
          <Loader2 size={22} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
          <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Loading team members…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="flex items-center justify-center w-14 h-14 rounded-lg"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
            <Users size={24} style={{ color: BRAND_COLORS.primary }} />
          </div>
          <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
            {search ? 'No members match your search' : 'No team members found'}
          </p>
          <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
            {search ? 'Try a different query' : 'Active team members will appear here'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map(member => (
              <TeamMemberCard
                key={member.id}
                member={member}
                onDelete={handleDelete}
                onAssignTask={id => navigate(`/tasks/assign/${id}`)}
                onViewProfile={handleViewProfile}
              />
            ))}
          </div>
          <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
            Showing {filtered.length} of {members.length} members
          </p>
        </>
      )}

      {/* Profile Modal */}
      {selectedProfile !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md flex flex-col max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>

            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-white/20">
                  <Users size={14} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Member Profile</p>
                  {selectedProfile?.name && (
                    <p className="text-[10px] text-white/70 truncate max-w-[180px]">{selectedProfile.name}</p>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedProfile(null)}
                className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20">
                <X size={14} className="text-white" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {profileLoading ? (
                <div className="flex items-center justify-center h-32 gap-3">
                  <Loader2 size={18} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
                  <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Loading profile…</span>
                </div>
              ) : (
                <div>
                  {/* Avatar + name block */}
                  <div className="flex items-center gap-4 mb-5 pb-4 border-b"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0 overflow-hidden"
                      style={{ backgroundColor: BRAND_COLORS.primary }}>
                      {selectedProfile.photo_url
                        ? <img src={selectedProfile.photo_url} alt="" className="w-full h-full object-cover" />
                        : (selectedProfile.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                        {selectedProfile.name || 'N/A'}
                      </p>
                      {selectedProfile.role && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                          {selectedProfile.role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Detail rows */}
                  <DetailRow label="Email"      value={selectedProfile.email} />
                  <DetailRow label="Phone"      value={selectedProfile.phone} />
                  <DetailRow label="Status"     value={selectedProfile.status} />
                  <DetailRow label="Joined"     value={fmtDate(selectedProfile.created_at)} />
                  <DetailRow label="Last Login" value={fmtDate(selectedProfile.last_login_at)} />
                  <DetailRow label="Last Active" value={fmtDate(selectedProfile.last_activity_at)} />

                  {Array.isArray(selectedProfile.skills) && selectedProfile.skills.length > 0 && (
                    <div className="flex items-start gap-3 py-2.5">
                      <span className="w-28 flex-shrink-0 text-xs font-semibold uppercase tracking-wide pt-1" style={{ color: SYSTEM_COLORS.textSoft }}>Skills</span>
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {selectedProfile.skills.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                            style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                            {typeof s === 'string' ? s : s.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex justify-end px-5 py-3 border-t"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
              <button
                onClick={() => setSelectedProfile(null)}
                className="px-4 py-2 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors"
                style={{ color: SYSTEM_COLORS.textMuted }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
